import { RoutePaths } from "@/core/constants/RoutePaths";
import { useAuth } from "@/core/hooks/useAuth";
import { mapAuthenticationError } from "@/shared/utils/mapAuthenticationError";
import { markLoginSubmitted } from "@/features/panel-load-timing/utils/panelLoadTiming";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import log from "loglevel";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export type LoginForm = {
  email: string;
  password: string;
};

const CREDENTIALS_ERROR_MESSAGE = "E-mail ou senha incorreta. Tente novamente.";

export const useLoginController = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [loading, { open: openLoading, close: closeLoading }] =
    useDisclosure(false);

  const form = useForm<LoginForm>({
    initialValues: {
      email: "",
      password: "",
    },
    validateInputOnBlur: true,
    validate: {
      email: (value) => (!value.trim() ? "E-mail é obrigatório" : null),
      password: (value) => (!value.trim() ? "Senha é obrigatória" : null),
    },
  });

  const [credentialsErrorActive, setCredentialsErrorActive] = useState(false);

  const clearCredentialsError = useCallback(() => {
    setCredentialsErrorActive(false);
    if (form.errors.password === CREDENTIALS_ERROR_MESSAGE) {
      form.clearFieldError("password");
    }
  }, [form]);

  const handleCredentialsFieldInteraction = useCallback(() => {
    if (!credentialsErrorActive) {
      return;
    }
    clearCredentialsError();
  }, [credentialsErrorActive, clearCredentialsError]);

  useEffect(() => {
    notifications.hide("sector-disconnected");
  }, []);

  const handleLogin = async () => {
    openLoading();
    const submittedAt = performance.now();
    try {
      const user = await login(form.values.email, form.values.password);
      clearCredentialsError();

      if (!user.sectorId) {
        navigate(RoutePaths.ONBOARDING, { replace: true });
      } else {
        markLoginSubmitted("password", submittedAt);
        navigate(RoutePaths.CHATS, { replace: true });
      }

      setTimeout(() => {
        notifications.show({
          title: "Login efetuado com sucesso!",
          message: "Bem-vindo(a)!",
          color: "teal",
          autoClose: 3000,
        });
      }, 100);

      form.reset();
    } catch (error) {
      log.error(error);

      const mappedError = mapAuthenticationError(error, "local-login");

      setCredentialsErrorActive(true);
      form.setFieldError("password", CREDENTIALS_ERROR_MESSAGE);

      notifications.show({
        title: "Não foi possível entrar",
        message: mappedError.message,
        color: "red",
        autoClose: 4000,
      });
    } finally {
      closeLoading();
    }
  };

  return {
    form,
    loading,
    handleLogin,
    credentialsErrorActive,
    credentialsErrorMessage: CREDENTIALS_ERROR_MESSAGE,
    handleCredentialsFieldInteraction,
  };
};
